import { Badge, Button, Group, LoadingOverlay, Modal, Stack, Table, Text, Tooltip } from '@mantine/core';
import { openConfirmModal } from '@mantine/modals';
import { showNotification } from '@mantine/notifications';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ZendeskSource, deleteZendeskSource, getZendeskSources } from '@mygpt/app';

import { AddSourceModal } from './AddSourceModal';
import { ZendeskOAuth } from '../../ZendeskOAuth';

const BUSY_STATUSES: ZendeskSource['status'][] = ['queued', 'importing', 'distilling'];
const STATUS_COLOR: Record<string, string> = {
  idle: 'gray',
  queued: 'blue',
  importing: 'blue',
  distilling: 'violet',
  imported: 'green',
  error: 'red',
};
const POLL_INTERVAL_MS = 10000;

const formatTime = (value?: string | null) => (value ? new Date(value).toLocaleString() : '-');

// 「ナレッジの継続改善 > ナレッジ取込」：Zendesk 工单来源的接入与设置。「取り込んで抽出」ボタンはナレッジ抽出 tab に置く
export const ZendeskSourcesList = () => {
  const { t } = useTranslation();
  const { datasetId } = useParams<{ datasetId: string }>();
  const [sources, setSources] = useState<ZendeskSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [addOpened, setAddOpened] = useState(false);
  const [authSource, setAuthSource] = useState<ZendeskSource | null>(null);
  const pollTimer = useRef<number | null>(null);

  const fetchSources = useCallback(
    async (silent = false) => {
      if (!datasetId) return;
      try {
        if (!silent) setLoading(true);
        const { data } = await getZendeskSources(datasetId);
        setSources(data.sources || []);
      } catch (error) {
        console.error('获取 Zendesk 数据源失败:', error);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [datasetId]
  );

  useEffect(() => {
    fetchSources();
  }, [fetchSources]);

  // 导入/蒸馏进行中时轮询进度
  useEffect(() => {
    const busy = sources.some((s) => BUSY_STATUSES.includes(s.status));
    if (busy && pollTimer.current === null) {
      pollTimer.current = window.setInterval(() => fetchSources(true), POLL_INTERVAL_MS);
    }
    if (!busy && pollTimer.current !== null) {
      window.clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
    return () => {
      if (pollTimer.current !== null) {
        window.clearInterval(pollTimer.current);
        pollTimer.current = null;
      }
    };
  }, [sources, fetchSources]);

  const handleDelete = (source: ZendeskSource) => {
    if (!datasetId) return;
    openConfirmModal({
      title: t('delete_btn'),
      centered: true,
      children: (
        <Text className="break-all">
          {t('dataset.zendesk.delete_confirm', { name: `${source.subdomain}.zendesk.com` })}
        </Text>
      ),
      labels: { confirm: t('delete_btn'), cancel: t('alert_cancel') },
      onCancel: () => {},
      confirmProps: {
        color: 'red',
        onClick: async () => {
          try {
            await deleteZendeskSource(datasetId, source.id);
            showNotification({ color: 'green', message: t('dataset.zendesk.delete_success') });
            fetchSources();
          } catch (error) {
            showNotification({ color: 'red', message: t('tip.delete_error') });
          }
        },
      },
    });
  };

  return (
    <section className="flex flex-col relative h-full">
      <section className="flex-auto">
        <section className="bg-white rounded-lg shadow-sm h-full flex flex-col">
          <Group justify="space-between" p="md" className="border-b">
            <Stack gap={2}>
              <Text fw={600}>{t('dataset.zendesk.title')}</Text>
              <Text size="xs" c="gray.6">
                {t('dataset.zendesk.description')}
              </Text>
            </Stack>
            <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setAddOpened(true)}>
              {t('dataset.zendesk.add_source')}
            </Button>
          </Group>
          <div className="flex-auto overflow-auto">
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('dataset.zendesk.col_connection')}</Table.Th>
                  <Table.Th>{t('embeddings.table.status')}</Table.Th>
                  <Table.Th>{t('dataset.zendesk.col_progress')}</Table.Th>
                  <Table.Th>{t('dataset.zendesk.col_faqs')}</Table.Th>
                  <Table.Th>{t('dataset.zendesk.col_last_run')}</Table.Th>
                  <Table.Th>{t('dataset.zendesk.col_actions')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <LoadingOverlay visible={loading} />
                {sources.length === 0 && !loading ? (
                  <Table.Tr>
                    <Table.Td colSpan={6} className="text-center py-8">
                      <Stack align="center" gap="md">
                        <Text c="gray.5">{t('no_data')}</Text>
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  sources.map((source) => {
                    const busy = BUSY_STATUSES.includes(source.status);
                    const stats = source.stats || {};
                    return (
                      <Table.Tr key={source.id}>
                        <Table.Td>
                          <Text size="sm" fw={500}>
                            {source.subdomain}.zendesk.com
                          </Text>
                          <Text size="xs" c="gray.6">
                            {source.friendly_name || source.admin_email}
                          </Text>
                          <Text size="xs" c="gray.5">
                            {t('dataset.zendesk.effective_publish', {
                              mode: t(`knowledge_loop.publish_mode.${source.effective_publish_mode || 'manual'}`),
                            })}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Badge color={STATUS_COLOR[source.status] || 'gray'} variant="light" size="sm">
                            {t(`dataset.zendesk.status_${source.status}`)}
                          </Badge>
                          {source.last_error && (
                            <Tooltip label={source.last_error} multiline w={320} withArrow>
                              <Text size="xs" c="red" lineClamp={1} style={{ maxWidth: 200 }}>
                                {source.last_error}
                              </Text>
                            </Tooltip>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs">
                            {t('dataset.zendesk.progress_line', {
                              seen: stats.tickets_seen ?? 0,
                              items: (stats.items_created ?? 0) + (stats.items_updated ?? 0),
                            })}
                          </Text>
                          {!!stats.pii_masked && (
                            <Text size="xs" c="orange.7">
                              {t('dataset.zendesk.pii_masked', { count: stats.pii_masked })}
                            </Text>
                          )}
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs">
                            {t('dataset.zendesk.faq_line', {
                              candidates: stats.distill_generalizable ?? 0,
                              created: stats.faqs_created ?? 0,
                            })}
                          </Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="xs">{formatTime(source.last_run_at)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Group gap="xs" wrap="nowrap">
                            <Button size="xs" variant="subtle" onClick={() => setAuthSource(source)}>
                              {t('zendesk_oauth.manage')}
                            </Button>
                            <Button
                              size="xs"
                              variant="subtle"
                              color="red"
                              leftSection={<IconTrash size={14} />}
                              disabled={busy}
                              onClick={() => handleDelete(source)}
                            >
                              {t('delete_btn')}
                            </Button>
                          </Group>
                        </Table.Td>
                      </Table.Tr>
                    );
                  })
                )}
              </Table.Tbody>
            </Table>
          </div>
        </section>
      </section>

      <Modal opened={!!authSource} onClose={() => setAuthSource(null)} title={t('zendesk_oauth.manage')} centered>
        {authSource && (
          <ZendeskOAuth
            key={authSource.connection_id}
            connectionId={authSource.connection_id}
            subdomain={authSource.subdomain}
            authentication={authSource.authentication}
            onAuthorized={() => {
              setAuthSource(null);
              fetchSources();
            }}
            onCredentialsSaved={() => {
              setAuthSource(null);
              fetchSources();
            }}
          />
        )}
      </Modal>
      {datasetId && (
        <AddSourceModal
          datasetId={datasetId}
          opened={addOpened}
          onClose={() => setAddOpened(false)}
          onCreated={() => {
            setAddOpened(false);
            fetchSources();
          }}
        />
      )}
    </section>
  );
};
